import { forwardRef } from "react";
import { useAuth } from "../../../../contexts/AuthContext";
import { Company } from "../../../../interfaces/company";
import { formatDecimals } from "../../../../utils/utils";
import {
  amountToWordsSoles,
  calculatePeruTicketData,
  formatPeruDate,
  formatPeruTime,
  getCustomerIdLabel,
  getQrImageSrc,
  getReferenceDocumentLabel,
  PERU_DOCUMENT_LABELS,
  PERU_PRINTED_REPRESENTATION,
  PERU_TICKET_CONFIG,
  PeruDocumentKind,
  PeruLineTotals,
  PeruTicketTotals,
} from "./invoiceTicketUtils";
import {
  buildCashierLabel,
  buildCompanyAddress,
  buildCustomerAddress,
  buildCustomerId,
  buildCustomerName,
  buildEstablishmentCode,
  buildExchangeRate,
  buildMachineNo,
  buildStoreAddress,
  buildStoreName,
  buildStorePhone,
} from "./invoiceTicketMappers";
import "./InvoiceTicket.css";

export interface PeruTicketBodyProps {
  documentKind: PeruDocumentKind;
  invoice: any;
  lines: any[];
  selectedClient?: any;
  selectedTPV?: any;
  empresaData?: Company | null;
  caja?: string | number;
  cajaInfo?: any;
}

const formatAmount = (value: number) =>
  `S/ ${formatDecimals(value, 2, undefined, true)}`;

const WideItemsTable = ({ lineTotals }: { lineTotals: PeruLineTotals[] }) => (
  <div className="pe-ticket-table-scroll">
    <table className="pe-ticket-items-table">
      <thead>
        <tr>
          <th>Item</th>
          <th>Cant.</th>
          <th>UM</th>
          <th>Código</th>
          <th>Cód. Sunat</th>
          <th>V. Unit</th>
          <th>P. Unit</th>
          <th>Dscto %</th>
          <th>V. Venta</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        {lineTotals.map((line) => (
          <tr key={`${line.itemNo}-${line.code}`}>
            <td>{line.itemNo}</td>
            <td className="num">
              {formatDecimals(line.quantity, 0, undefined, true)}
            </td>
            <td>{line.unitOfMeasure}</td>
            <td>{line.code}</td>
            <td>{line.sunatCode || "-"}</td>
            <td className="num">
              {formatDecimals(line.vUnit, 2, undefined, true)}
            </td>
            <td className="num">
              {formatDecimals(line.pUnit, 2, undefined, true)}
            </td>
            <td className="num">
              {formatDecimals(line.discountPct, 2, undefined, true)}
            </td>
            <td className="num">
              {formatDecimals(line.saleValue, 2, undefined, true)}
            </td>
            <td className="desc">{line.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CompactItemsTable = ({ lineTotals }: { lineTotals: PeruLineTotals[] }) => (
  <div className="pe-ticket-table-scroll">
    <table className="pe-ticket-items-table pe-ticket-items-table-compact">
      <thead>
        <tr>
          <th>Item</th>
          <th>Código</th>
          <th>UM</th>
          <th>V. Unit</th>
          <th>Cód. Sunat</th>
          <th>Dscto %</th>
          <th>V. Venta</th>
          <th>Descripción</th>
          <th>Cant.</th>
          <th>P. Unit</th>
        </tr>
      </thead>
      <tbody>
        {lineTotals.map((line) => (
          <tr key={`${line.itemNo}-${line.code}`}>
            <td>{line.itemNo}</td>
            <td>{line.code}</td>
            <td>{line.unitOfMeasure}</td>
            <td className="num">
              {formatDecimals(line.vUnit, 2, undefined, true)}
            </td>
            <td>{line.sunatCode || "-"}</td>
            <td className="num">
              {formatDecimals(line.discountPct, 2, undefined, true)}
            </td>
            <td className="num">
              {formatDecimals(line.saleValue, 2, undefined, true)}
            </td>
            <td className="desc">{line.description}</td>
            <td className="num">
              {formatDecimals(line.quantity, 0, undefined, true)}
            </td>
            <td className="num">
              {formatDecimals(line.pUnit, 2, undefined, true)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TotalsBox = ({
  totals,
  showDiscount,
}: {
  totals: PeruTicketTotals;
  showDiscount: boolean;
}) => (
  <div className="pe-ticket-totals-box">
    {totals.taxableBase > 0 && (
      <div className="pe-ticket-totals-row">
        <span className="label">
          Total Valor de Venta - Operaciones Gravadas:
        </span>
        <span className="amount">{formatAmount(totals.taxableBase)}</span>
      </div>
    )}
    {totals.exemptBase > 0 && (
      <div className="pe-ticket-totals-row">
        <span className="label">
          Total Valor de Venta - Operaciones Exoneradas:
        </span>
        <span className="amount">{formatAmount(totals.exemptBase)}</span>
      </div>
    )}
    <div className="pe-ticket-totals-row">
      <span className="label">
        IGV ({formatDecimals(totals.igvPercent, 2)} %):
      </span>
      <span className="amount">{formatAmount(totals.igv)}</span>
    </div>
    <div className="pe-ticket-totals-row total-final">
      <span className="label">Importe Total:</span>
      <span className="amount">{formatAmount(totals.total)}</span>
    </div>
    {showDiscount && totals.totalDiscount > 0 && (
      <div className="pe-ticket-totals-row">
        <span className="label">Total Descuento</span>
        <span className="amount">{formatAmount(totals.totalDiscount)}</span>
      </div>
    )}
  </div>
);

const ReferenceBlock = ({ invoice }: { invoice: any }) => (
  <>
    <div className="pe-ticket-divider" />
    <div className="pe-ticket-ref-title">Doc. Referencia</div>
    <div className="pe-ticket-row">
      <span className="pe-ticket-label">Doc. Referencia :</span>
      <span className="pe-ticket-value">
        {getReferenceDocumentLabel(invoice)}
      </span>
    </div>
    <div className="pe-ticket-row">
      <span className="pe-ticket-label">Número Doc. Referencia :</span>
      <span className="pe-ticket-value">{invoice?.Applies_to_Doc_No || "-"}</span>
    </div>
    <div className="pe-ticket-row">
      <span className="pe-ticket-label">Fecha de Emisión :</span>
      <span className="pe-ticket-value">
        {formatPeruDate(
          invoice?.Applies_to_Posting_Date ||
            invoice?.Posting_Date_Ref ||
            invoice?.Posting_Date,
        )}
      </span>
    </div>
    <div className="pe-ticket-row">
      <span className="pe-ticket-label">Cod. Tipo Nota de Crédito :</span>
      <span className="pe-ticket-value">
        {invoice?.Cod_Tipo_Nota_Credito || invoice?.Credit_Memo_Type || "06"}
      </span>
    </div>
    <div className="pe-ticket-row">
      <span className="pe-ticket-label">Tipo de Nota de Crédito :</span>
      <span className="pe-ticket-value">
        {invoice?.Tipo_Nota_Credito || "Devolución total"}
      </span>
    </div>
    <div className="pe-ticket-row">
      <span className="pe-ticket-label">Sustento :</span>
      <span className="pe-ticket-value">
        {invoice?.Sustento_Nota_Credito || "DEVOLUCION TOTAL"}
      </span>
    </div>
  </>
);

const PolicyBlock = () => (
  <div className="pe-ticket-policy">
    <p>NO SE ACEPTARÁ LA BOLSA PLÁSTICA EN LA DEVOLUCIÓN.</p>
    <p>ACÉRCATE CON TU DOCUMENTO DE IDENTIDAD (DNI O CE).</p>
    <p>
      EL PRODUCTO SE DEBE DE ENCONTRAR SELLADO, EN EL EMPAQUE ORIGINAL O EN
      BUEN ESTADO.
    </p>
    <p>
      DEBERÁS PRESENTAR LA BOLETA EN FÍSICO O VIRTUAL DE LA COMPRA GENERADA.
    </p>
    <p>
      NO CONTAR CON MÁS DE 15 DÍAS CALENDARIO DE HABER EFECTUADO LA COMPRA.
    </p>
  </div>
);

const PeruTicketBody = forwardRef<HTMLDivElement, PeruTicketBodyProps>(
  (
    {
      documentKind,
      invoice,
      lines = [],
      selectedClient,
      selectedTPV,
      empresaData,
      caja,
      cajaInfo,
    },
    ref,
  ) => {
    const { currentUser } = useAuth() as any;
    const config = PERU_TICKET_CONFIG[documentKind];
    const customerIdLabel = getCustomerIdLabel(invoice, selectedClient);
    const { lineTotals, totals } = calculatePeruTicketData(lines);
    // TODO: Dato backend - imagen QR SUNAT (invoice.GenerateQR / invoice.QR_Code)
    const qrSrc = getQrImageSrc(invoice);

    const companyAddress = buildCompanyAddress(empresaData);
    const storeName = buildStoreName(selectedTPV, invoice, cajaInfo || caja);
    const storeAddress = buildStoreAddress(selectedTPV);
    const storePhone = buildStorePhone(selectedTPV, empresaData);

    return (
      <div className="pe-ticket-container">
        <div className="pe-ticket-content" ref={ref}>
          <div className="pe-ticket-company-name">
            {empresaData?.Name || "-"}
          </div>
          <div className="pe-ticket-center">{companyAddress || "-"}</div>
          <div className="pe-ticket-center">Tienda : {storeName}</div>
          {storeAddress && (
            <div className="pe-ticket-center">{storeAddress}</div>
          )}
          <div className="pe-ticket-center">Teléfono : {storePhone}</div>

          <div className="pe-ticket-ruc">
            RUC : {empresaData?.VAT_Registration_No || "-"}
          </div>

          <div className="pe-ticket-doc-type">
            {PERU_DOCUMENT_LABELS[documentKind]}
          </div>
          <div className="pe-ticket-doc-number">{invoice?.No || "-"}</div>

          <div className="pe-ticket-row">
            <span className="pe-ticket-label">Maq. Reg. Nro :</span>
            <span className="pe-ticket-value">
              {buildMachineNo(selectedTPV, invoice)}
            </span>
          </div>

          <div className="pe-ticket-row-inline">
            <span>
              Fecha Emisión :{" "}
              {formatPeruDate(invoice?.Posting_Date || invoice?.Document_Date)}
            </span>
            <span>: Hora : {formatPeruTime(invoice)}</span>
          </div>

          <div className="pe-ticket-row">
            <span className="pe-ticket-label">Señor(es) :</span>
            <span className="pe-ticket-value">
              {buildCustomerName(invoice, selectedClient)}
            </span>
          </div>

          <div className="pe-ticket-row">
            <span className="pe-ticket-label">{customerIdLabel} :</span>
            <span className="pe-ticket-value">
              {buildCustomerId(invoice, selectedClient)}
            </span>
          </div>

          <div className="pe-ticket-row">
            <span className="pe-ticket-label">Dirección :</span>
            <span className="pe-ticket-value">
              {buildCustomerAddress(invoice, selectedClient)}
            </span>
          </div>

          <div className="pe-ticket-row">
            <span className="pe-ticket-label">Moneda :</span>
            <span className="pe-ticket-value">
              {invoice?.Currency_Code || "SOL"}
            </span>
          </div>

          {config.showReferenceBlock && <ReferenceBlock invoice={invoice} />}

          {config.tableLayout === "wide" ? (
            <WideItemsTable lineTotals={lineTotals} />
          ) : (
            <CompactItemsTable lineTotals={lineTotals} />
          )}

          <TotalsBox
            totals={totals}
            showDiscount={config.showDiscountInTotals}
          />

          <div className="pe-ticket-amount-words">
            {amountToWordsSoles(totals.total)}
          </div>

          <div className="pe-ticket-row">
            <span className="pe-ticket-label">Num. Artículos :</span>
            <span className="pe-ticket-value">
              {formatDecimals(totals.itemCount, 2, undefined, true)}
            </span>
          </div>
          <div className="pe-ticket-row">
            <span className="pe-ticket-label">Cajero :</span>
            <span className="pe-ticket-value">
              {buildCashierLabel(invoice, currentUser)}
            </span>
          </div>
          <div className="pe-ticket-row">
            <span className="pe-ticket-label">T. Cambio :</span>
            <span className="pe-ticket-value">
              {buildExchangeRate(invoice)}
            </span>
          </div>

          <div className="pe-ticket-note">
            NOTA: PARA CAMBIOS Y/O DEVOLUCIONES PRESENTAR EL COMPROBANTE DE PAGO
          </div>

          {/* TODO: Dato backend - imagen QR SUNAT */}
          {qrSrc && (
            <div className="pe-ticket-qr">
              <img src={qrSrc} alt="Código QR SUNAT" />
            </div>
          )}

          <div className="pe-ticket-legal">
            {PERU_PRINTED_REPRESENTATION[documentKind]}
          </div>
          <div className="pe-ticket-legal">
            Autorizado para ser Emisor electrónico mediante la Resolución de
            Intendencia N° 0180050001416/SUNAT
          </div>
          <div className="pe-ticket-legal">
            Para consultar el comprobante ingresar a:
            <br />
            <a
              href={config.consultationUrl}
              target="_blank"
              rel="noreferrer"
            >
              {config.consultationUrl.replace(/^https?:\/\//, "")}
            </a>
          </div>
          <div className="pe-ticket-legal">
            Cod. Establecimiento : {buildEstablishmentCode(invoice)}
          </div>

          {config.showPolicy && <PolicyBlock />}
        </div>
      </div>
    );
  },
);

PeruTicketBody.displayName = "PeruTicketBody";

export default PeruTicketBody;
